import { AppDispatch, AppState } from 'app/store';
import { postAPI } from 'services/api/apiAsync';
import { selectEnrollmentToken } from 'services/enrollment/enrollmentSelectors';
import { API_ENDPOINTS } from 'utils/constants';
import { logErrors } from 'utils';
import { setMarketingAttribution } from './marketingSlice';
import {
  selectFreeSpiritNumber,
  selectHasCapturedMarketing,
  selectMarketingParams,
} from './marketingSelectors';
import parseMarketingQueryParams from './utils';

/**
 * Reads the landing URL's marketing attribution into the store, once per session.
 *
 * identityWeb does this in `app.component.ts` on every `queryParams` emission; here the
 * capture is guarded so an in-app navigation — which no longer carries the params — cannot
 * overwrite the original attribution with an empty set.
 */
export function captureMarketingAttribution(search: string) {
  return (dispatch: AppDispatch, getState: AppState) => {
    if (selectHasCapturedMarketing(getState())) {
      return;
    }
    dispatch(setMarketingAttribution(parseMarketingQueryParams(search)));
  };
}

/**
 * Reports the captured attribution against the new enrollment.
 *
 * Fire-and-forget, matching identityWeb (`enrollment.service.ts` calls it straight after the
 * account is created and never surfaces a failure): the member must not be blocked because an
 * analytics write failed. Authenticated by the enrollment token, not a bearer token.
 */
export function createMarketingAnalytics() {
  return async (dispatch: AppDispatch, getState: AppState) => {
    try {
      const state = getState();
      const params = selectMarketingParams(state);
      const enrollmentToken = selectEnrollmentToken(state);

      /* Nothing to report, and the endpoint rejects a call without the token. */
      if (!params || !Object.keys(params).length || !enrollmentToken) {
        return undefined;
      }

      const config = {
        url: API_ENDPOINTS.marketingAnalytics,
        data: params,
        headers: {
          'x-pdr-enrollment-token': enrollmentToken,
        },
      };
      const response = await dispatch(postAPI(config)).unwrap();
      return response;
    } catch (error) {
      logErrors(error);
      return undefined;
    }
  };
}

/**
 * Links a Spirit Airlines Free Spirit number to the newly created member so the promotion
 * credits. identityWeb fires this from both billing paths after the payment succeeds
 * (`enrollment.service.ts:673` and `:712`) and, like the analytics call, ignores failures.
 */
export function syncFreeSpiritNumber(userId: string) {
  return async (dispatch: AppDispatch, getState: AppState) => {
    try {
      const freeSpiritNumber = selectFreeSpiritNumber(getState());

      if (!freeSpiritNumber || !userId) {
        return undefined;
      }

      const config = {
        url: API_ENDPOINTS.spiritAirlinesPromotions,
        data: { fsn: freeSpiritNumber, userId },
      };
      const response = await dispatch(postAPI(config)).unwrap();
      return response;
    } catch (error) {
      logErrors(error);
      return undefined;
    }
  };
}
